import { TruexAdRendererParameters } from '@truex/ad-renderer';
// @ts-ignore
import processEnv from 'processEnv';
import { AdCuePoint } from '../ad-managers/AbstractAdManager';
import {
  getIdvxStub,
  getTruexVastConfigStub,
  getTruexVastUrlStub,
} from './InteractiveAdStubs';

export type InteractiveAdData = {
  vastConfig: TruexAdRendererParameters;
  adEndTime: number; // Timestamp to skip to for certain interactive ad flows
  cuePoint: AdCuePoint;
  ignoreAdDuration: boolean; // TrueX ads will want to have their ad duration ignored for timestamp bookkeeping
  duration: number;
  adType: InteractiveAdType;
};

export type InteractiveAdType = 'TrueX' | 'IDVx';

export const getInteractiveAdData = (
  ad: google.ima.Ad,
  cuePoint: AdCuePoint | null,
  rawTime: number,
): InteractiveAdData | null => {
  const pod = ad.getAdPodInfo();

  // Stubs may need to be regenerated periodically because server can eventually kill tags
  if (
    processEnv.NODE_ENV !== 'prod' &&
    processEnv['APP_FORCE_TRUEX_STUBS'] === 'true'
  ) {
    if (pod.getAdPosition() === 1) {
      // More relevant payload structure if available
      let stub = getTruexVastConfigStub(ad, cuePoint, rawTime);
      if (!stub?.vastConfig) {
        stub = getTruexVastUrlStub(ad, cuePoint, rawTime);
      }

      return stub;
    }
    if (pod.getAdPosition() === 2) {
      return getIdvxStub(ad, cuePoint, rawTime);
    }
  }

  if (!cuePoint) return null;
  const adSystem = ad.getAdSystem().toLowerCase();
  const isTruexAd = adSystem == 'truex' && pod.getAdPosition() == 1;
  const isIdvxAd = adSystem == 'idvx';
  if (!isTruexAd && !isIdvxAd) return null;
  return parseInteractiveAd({
    ad,
    slotStartTime: rawTime,
    cuePoint,
    adType: isTruexAd ? 'TrueX' : 'IDVx',
  });
};

const parseInteractiveAd = (data: {
  ad: google.ima.Ad;
  slotStartTime: number;
  cuePoint: AdCuePoint;
  adType: InteractiveAdType;
}) => {
  const { ad, slotStartTime, cuePoint, adType } = data;

  const rawParameters = (ad.getTraffickingParametersString() || '').trim();
  const vastJson: TruexAdRendererParameters = rawParameters
    ? JSON.parse(rawParameters)
    : null;
  const duration = ad.getDuration();

  let vastUrl = ad.getDescription();
  if (vastUrl) {
    vastUrl = vastUrl.trim();
    if (!vastUrl.startsWith('http')) {
      vastUrl = 'https://' + vastUrl;
    }
  }

  if (!vastJson && !vastUrl) return null;

  const result: InteractiveAdData = {
    vastConfig: vastJson || vastUrl, // Interactives can come in one of two ways.  Prioritize complex object
    adEndTime: duration + slotStartTime,
    cuePoint,
    ignoreAdDuration: adType === 'TrueX',
    duration,
    adType,
  };

  return result;
};

export enum InteractiveAdCompleteType {
  ERROR = 'error',
  NO_ADS_AVAILABLE = 'no ads',
  COMPLETE = 'complete',
}
